import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CommonDropdown from "../../../components/commonDropDown/CommonDropDown";
import CommonTextField from "../../../components/commonTextField/CommonTextField";
import { countries, genders, ageGroup, race } from "../../../constants/constants";
import { updateStep, clearValidation } from "../../../redux/profileFormSlice";
import ValidationError from "../../../components/ValidationError/ValidationError";

const schema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  gender: yup.string(),
  country: yup.string().required("Country is required"),
  state: yup.string().required("State/Province is required"),
  city: yup.string().required("City is required"),
  age: yup.string(),
  race: yup.string(),
});

const PersonalInformation = () => {
  const dispatch = useDispatch();
  const { email, firstName, lastName } = useSelector((state) => state.auth);
  const stepData = useSelector((state) => state.profileForm.step3);
  const validationStep = useSelector((state) => state.profileForm.validationStep);

  const {
    register,
    reset,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      firstName: stepData?.firstName || firstName || "",
      lastName: stepData?.lastName || lastName || "",
      country: stepData?.country || "",
      state: stepData?.state || "",
      city: stepData?.city || "",
      age: stepData?.age || "",
      race: stepData?.race || "",
      email: email || "",
    },
  });

  useEffect(() => {
    const subscription = watch((values) => {
      dispatch(updateStep({ step: "step3", data: values }));
    });
    return () => subscription.unsubscribe();
  }, [watch, dispatch]);

  useEffect(() => {
    const initialValues = getValues();
    if (initialValues) {
      dispatch(updateStep({ step: "step3", data: initialValues }));
    }
  }, []);

  useEffect(() => {
    if (validationStep === 3) {
      trigger();
      dispatch(clearValidation());
    }
  }, [validationStep, trigger, dispatch]);

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div>
      <div className="text-center mb-5">
        <h3 className="text-lg sm:text-xl font-bold text-gray-800">Personal Information</h3>
        <p className="text-xs sm:text-sm text-gray-500">Fill in your basic personal details</p>
      </div>

      <ValidationError message={hasErrors ? "Please fill in all required fields" : null} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <CommonTextField name="firstName" label="First Name" register={register} errors={errors} required defaultValue={stepData?.firstName || firstName || ""} />
        <CommonTextField name="lastName" label="Last Name" register={register} errors={errors} required defaultValue={stepData?.lastName || lastName || ""} />
        <CommonDropdown name="country" label="Country" register={register} errors={errors} options={countries} required defaultValue={stepData?.country} search />
        <CommonTextField name="city" label="City" register={register} errors={errors} required defaultValue={stepData?.city} />
        <CommonTextField name="state" label="State/Province" register={register} errors={errors} required defaultValue={stepData?.state} />
        <CommonDropdown name="gender" label="Gender" register={register} errors={errors} options={genders} defaultValue={stepData?.gender} />
        <CommonDropdown name="age" label="Age Group" register={register} errors={errors} options={ageGroup} defaultValue={stepData?.age} />
        <CommonDropdown name="race" label="Race/Ethnicity" register={register} errors={errors} options={race} defaultValue={stepData?.race} search />
      </div>

      <div className="mt-4 sm:mt-6">
        <CommonTextField
          name="email"
          label="Email"
          register={register}
          errors={errors}
          required
          readOnly
          defaultValue={email}
        />
      </div>
    </div>
  );
};

export default PersonalInformation;
